// League rules as Zod schemas. See docs/design/contracts/RULES_SCHEMA.md.
//
// Shape-only validation lives here. Anything that needs the sport's tables
// (is this stat scorable? does this slot exist?) or crosses fields (do the
// playoffs fit?) lives in ./validate.ts so the editor can report every
// problem at once.

import { z } from 'zod';

export const SETTINGS_SCHEMA_VERSION = 3 as const;

/**
 * Stored blobs this schema can still read, oldest first.
 *
 * Version 1 kept league awards and account achievements in one `fun.awards`
 * object. Version 2 kept the draft's format, pick order and mode in one
 * `draft.type` (the 2026-09-19 decision, BACKLOG D1). `StoredLeagueSettings`
 * at the bottom of this file upgrades either on read; nothing rewrites a
 * stored row, because a settings version is a record of what the league
 * agreed to at the time.
 */
export const READABLE_SETTINGS_VERSIONS = [1, 2, 3] as const;

const Hex = z.string().regex(/^#[0-9a-f]{6}$/i, 'Use #rrggbb');
const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
const Iso = z.string().datetime({ offset: true });
const PeriodIndexSchema = z.number().int().min(1).max(60);

export const SportIdSchema = z.enum(['football', 'basketball', 'baseball', 'hockey']);
export const LeagueFormatIdSchema = z.enum([
  'h2h-points',
  'h2h-categories',
  'h2h-each-category',
  'rotisserie',
  'points-total',
  'all-play',
  'best-ball',
  'elimination',
]);
export const LineupPeriodKindSchema = z.enum(['weekly', 'daily']);
export const LockRuleIdSchema = z.enum(['per-player-game', 'first-game', 'fixed-time']);

// ── Identity ──────────────────────────────────────────────────────────────

export const IdentitySettings = z.object({
  name: z.string().trim().min(1).max(60),
  teamCount: z.number().int().min(2).max(20),
  description: z.string().max(500).default(''),
  branding: z
    .object({
      accentColor: Hex.nullable().default(null),
      // TODO(B11): nothing stores an image yet, so these stay null.
      logoAssetId: z.string().nullable().default(null),
      bannerAssetId: z.string().nullable().default(null),
      trophyName: z.string().max(40).default('League Champion'),
    })
    .default({
      accentColor: null,
      logoAssetId: null,
      bannerAssetId: null,
      trophyName: 'League Champion',
    }),
  /** IANA zone. Fixed-time locks, waiver processing, digests all use it. */
  timeZone: z.string().min(1),
});

// ── Format ────────────────────────────────────────────────────────────────

export const FormatSettings = z.object({
  id: LeagueFormatIdSchema,
  h2h: z
    .object({
      medianGame: z.boolean().default(false),
      twoOpponents: z.boolean().default(false),
      doubleHeaderPeriods: z.array(PeriodIndexSchema).default([]),
    })
    .default({ medianGame: false, twoOpponents: false, doubleHeaderPeriods: [] }),
  elimination: z
    .object({
      eliminatePerPeriod: z.number().int().min(1).default(1),
      eliminatedRosterTo: z.enum(['waivers', 'free-agency']).default('waivers'),
      stopWhenTeamsRemain: z.number().int().min(1).default(1),
    })
    .nullable()
    .default(null),
});

// ── Scoring ───────────────────────────────────────────────────────────────

const Positions = z.array(z.string().min(1)).nullable().default(null);

export const UnitRule = z.object({
  stat: z.string().min(1),
  points: z.number(),
  per: z.number().positive().default(1),
  rounding: z.enum(['none', 'floor', 'round']).default('none'),
  positions: Positions,
});

export const BonusRule = z.object({
  stat: z.string().min(1),
  threshold: z.number(),
  points: z.number(),
  scope: z.enum(['game', 'period']).default('game'),
  positions: Positions,
});

export const TierBand = z.object({
  min: z.number(),
  max: z.number().nullable(),
  points: z.number(),
});

export const TierRule = z.object({
  stat: z.string().min(1),
  bands: z.array(TierBand).min(1),
  scope: z.enum(['game', 'period']).default('game'),
  positions: Positions,
});

export const ScoringConfig = z.object({
  presetId: z.string().nullable().default(null),
  units: z.array(UnitRule),
  bonuses: z.array(BonusRule).default([]),
  tiers: z.array(TierRule).default([]),
  decimals: z.number().int().min(0).max(2).default(2),
});

// ── Categories ────────────────────────────────────────────────────────────

export const CategoryConfig = z.object({
  presetId: z.string().nullable().default(null),
  categories: z
    .array(z.object({ stat: z.string().min(1), weight: z.number().positive().default(1) }))
    .min(1),
  ties: z.enum(['half', 'ignore']).default('half'),
});

// ── Roster ────────────────────────────────────────────────────────────────

export const RosterConfig = z.object({
  presetId: z.string().nullable().default(null),
  slots: z
    .array(z.object({ slot: z.string().min(1), count: z.number().int().min(0).max(20) }))
    .min(1),
  maxPerPosition: z.record(z.string(), z.number().int().min(0)).default({}),
  reserve: z
    .object({
      slot: z.string().min(1),
      count: z.number().int().min(0),
      eligibleStatuses: z.array(z.string().min(1)),
      mustMoveWhenHealthy: z.boolean().default(true),
    })
    .nullable()
    .default(null),
  taxi: z
    .object({
      slot: z.string().min(1),
      count: z.number().int().min(0),
      maxYearsExperience: z.number().int().min(0),
      // The week whose first kickoff closes the squad, Sleeper's taxi
      // deadline (PLAN-2026-10-27-the-next-season §2.7 rule 4); null is the
      // league's first week.
      deadlinePeriod: PeriodIndexSchema.nullable().default(null),
    })
    .nullable()
    .default(null),
});

// ── Lineup ────────────────────────────────────────────────────────────────

export const LineupConfig = z.object({
  period: LineupPeriodKindSchema,
  lockRule: LockRuleIdSchema,
  fixedLockTime: ClockTime.nullable().default(null),
  gamesPlayedCaps: z
    .array(
      z.object({
        positions: z.array(z.string().min(1)).min(1),
        maxGames: z.number().int().min(1),
        per: z.enum(['period', 'season']),
      }),
    )
    .default([]),
  bestBall: z.boolean().default(false),
  allowEmptySlots: z.boolean().default(true),
});

// ── Schedule and playoffs ─────────────────────────────────────────────────

export const ScheduleSettings = z.object({
  regularSeason: z.object({ firstPeriod: PeriodIndexSchema, lastPeriod: PeriodIndexSchema }),
  divisions: z
    .array(z.object({ id: z.string().min(1), name: z.string().min(1).max(30) }))
    .default([]),
  divisionGamesFirst: z.boolean().default(true),
  rivalryPeriods: z.array(PeriodIndexSchema).default([]),
  generated: z.enum(['auto', 'manual']).default('auto'),
});

export const PlayoffSettings = z.object({
  teamCount: z.number().int().min(0).max(16),
  periods: z.array(PeriodIndexSchema).default([]),
  byes: z.number().int().min(0).default(0),
  reseed: z.boolean().default(false),
  championshipPeriods: z.number().int().min(1).max(2).default(1),
  seeding: z.enum(['standings', 'division-winners-first']).default('standings'),
  consolation: z.enum(['none', 'bracket', 'toilet-bowl']).default('none'),
});

// ── Tiebreakers ───────────────────────────────────────────────────────────

export const TiebreakerId = z.enum([
  'head-to-head',
  'points-for',
  'points-against',
  'division-record',
  'record-vs-common',
  'all-play-record',
  'points-for-last-3',
  'coin-flip',
]);

export const TiebreakerSettings = z.object({
  standings: z.array(TiebreakerId).min(1),
  matchup: z.enum(['tie', 'bench-points', 'home-team', 'coin-flip']).default('tie'),
});

// ── Waivers ───────────────────────────────────────────────────────────────

export const WaiverSettings = z.object({
  system: z.enum(['none', 'reverse-standings', 'rolling', 'faab']),
  faab: z
    .object({
      budget: z.number().int().min(0),
      minBid: z.number().int().min(0).default(0),
      tiebreak: z
        .enum(['waiver-priority', 'reverse-standings', 'earlier-claim'])
        .default('waiver-priority'),
      allowZeroBids: z.boolean().default(true),
    })
    .nullable()
    .default(null),
  processing: z.object({
    schedule: z.enum(['daily', 'weekly', 'continuous']),
    time: ClockTime,
    weekday: z.number().int().min(0).max(6).nullable().default(null),
  }),
  claimPeriodHours: z.number().int().min(0).default(24),
  droppedPlayersGoToWaivers: z.boolean().default(true),
  gameTimeLock: z.boolean().default(true),
  postDraftWaiverHours: z.number().int().min(0).default(24),
  conditionalClaims: z.boolean().default(true),
});

/**
 * The two answers the create form gives to "how do waivers work?".
 *
 * CUT FROM `WaiverSettings.system`, not written out again, so a system cannot
 * be offered at creation that the rules do not have. `none` and `rolling`
 * stay settings a league can hold without being questions a new league is
 * asked: the worst team first is the default and a budget the one other
 * choice (PLAN-2026-09-24-free-agents-and-waivers §2 rule 1).
 */
export const WaiverSystemAnswer = WaiverSettings.shape.system.extract([
  'reverse-standings',
  'faab',
]);
export type WaiverSystemAnswer = z.infer<typeof WaiverSystemAnswer>;

export const AcquisitionLimits = z.object({
  perPeriod: z.number().int().min(0).nullable().default(null),
  perSeason: z.number().int().min(0).nullable().default(null),
  perPosition: z.record(z.string(), z.number().int().min(0)).default({}),
});

// ── Trades ────────────────────────────────────────────────────────────────

export const TradeSettings = z.object({
  review: z.enum(['none', 'commissioner', 'league-vote']),
  reviewWindowHours: z.number().int().min(0).default(24),
  vetoThreshold: z
    .object({ kind: z.enum(['count', 'fraction']), value: z.number().positive() })
    .nullable()
    .default(null),
  deadlinePeriod: PeriodIndexSchema.nullable().default(null),
  assets: z
    .object({
      players: z.literal(true).default(true),
      draftPicks: z.boolean().default(false),
      faab: z.boolean().default(false),
    })
    .default({ players: true, draftPicks: false, faab: false }),
  maxPlayersPerSide: z.number().int().min(1).max(10).default(5),
  allowDuringPlayoffs: z.boolean().default(false),
  requireRosterLegalAfter: z.boolean().default(true),
});

// ── Draft ─────────────────────────────────────────────────────────────────

/**
 * How every manager in the league ranks players for the autopick draft — the
 * commissioner's choice, made when the league is created and settled before
 * anyone ranks, never a manager's. DRAFT_MODES §4.1.
 *
 * - `overall`: one list, best first, with a fewest and a most at each position
 *   (ESPN's and Yahoo's model).
 * - `by-position`: a list per position, and a position for every round
 *   (NFL.com's positions by round). The pick order says how many of each.
 */
export const DraftRanking = z.enum(['overall', 'by-position']);
export type DraftRanking = z.infer<typeof DraftRanking>;

/**
 * The draft's three questions, one field each ([the 2026-09-19 decision](
 * ../../../../docs/decisions/2026-09-19-draft-settings-shape.md), DRAFT_MODES
 * §2): how a player is acquired, who picks when, and who is present and who
 * enters it. One `type` held all three until settings version 3.
 */
export const DraftFormat = z.enum(['pick', 'auction']);
export type DraftFormat = z.infer<typeof DraftFormat>;
/** Who picks when, for the `pick` format: the round's order and how it turns. */
export const DraftOrder = z.enum(['snake', 'linear', 'third-round-reversal']);
export type DraftOrder = z.infer<typeof DraftOrder>;
/**
 * - `autopick`: nobody attends; every manager ranks beforehand and the site
 *   drafts at the time (DRAFT_MODES §3.2).
 * - `manual`: drafted in person and entered by the commissioner afterward
 *   (§3.3, scenario C5).
 * - `live`: everybody in the room on a pick clock (§3.1, D10).
 */
export const DraftMode = z.enum(['live', 'autopick', 'manual']);
export type DraftMode = z.infer<typeof DraftMode>;
/**
 * A live draft's room opens half an hour before it (`DraftView.roomOpen`):
 * one rule for the room, Home and the calendar.
 */
export const DRAFT_ROOM_OPENS_MS = 30 * 60 * 1000;
/**
 * How the picks are made, in one word: the pick order, or `auction`. What the
 * `drafts` row stores as `type` and the wire calls `draftType`, since a
 * client asking how a draft runs wants one answer; the settings keep the two
 * questions apart.
 */
export const DraftType = z.enum(['snake', 'linear', 'third-round-reversal', 'auction']);
export type DraftType = z.infer<typeof DraftType>;

/** The one word for a draft's format and order (`DraftType`). */
export const draftTypeOf = (draft: { format: DraftFormat; order: DraftOrder | null }): DraftType =>
  draft.format === 'auction' ? 'auction' : (draft.order ?? 'snake');

export const DraftSettings = z.object({
  format: DraftFormat.default('pick'),
  /** Null for an auction, which has no pick order; the validator refuses either mismatch. */
  order: DraftOrder.nullable().default('snake'),
  mode: DraftMode.default('autopick'),
  scheduledAt: Iso.nullable().default(null),
  /** Who draws the first pick. `order` until settings version 3. */
  seatOrder: z.enum(['random', 'manual', 'reverse-standings']).default('random'),
  manualOrder: z.array(z.string()).default([]),
  rounds: z.number().int().min(1).max(40),
  pickSeconds: z
    .number()
    .int()
    .min(15)
    .max(24 * 3600),
  overnightPause: z.object({ from: ClockTime, to: ClockTime }).nullable().default(null),
  /** What a live pick clock does when it runs out. `autopick` until settings version 3. */
  whenTheClockExpires: z
    .enum(['queue-then-rankings', 'rankings', 'off'])
    .default('queue-then-rankings'),
  /** Defaulted, so every league stored before 2026-10-08 reads as one list. */
  ranking: DraftRanking.default('overall'),
  /** An auction's rules (AUCTION.md §9); null for a pick draft. */
  auction: z
    .object({
      budget: z.number().int().min(1),
      minBid: z.number().int().min(0).default(1),
      nominationSeconds: z.number().int().min(10),
      bidSeconds: z.number().int().min(5),
      /**
       * A bid with less than this left puts the bid clock back to it: ten
       * seconds live, half the clock in a slow auction. Defaulted, so a block
       * stored before 2026-10-15 reads as every site's ten.
       */
      resetSeconds: z.number().int().min(1).default(10),
      /** Players up at once: one live, several in a slow auction (§5). */
      lotsAtOnce: z.number().int().min(1).max(10).default(1),
      /** Money left over joins the waiver budget when the draft ends (§3.7). */
      leftoverToWaivers: z.boolean().default(false),
    })
    .nullable()
    .default(null),
  pickTradingAllowed: z.boolean().default(false),
});

// ── Keepers ───────────────────────────────────────────────────────────────

export const KeeperSettings = z.object({
  mode: z.enum(['redraft', 'keeper', 'dynasty']),
  keeperCount: z.number().int().min(0).default(0),
  cost: z.enum(['none', 'draft-round', 'draft-round-escalating', 'auction-value']).default('none'),
  escalationRounds: z.number().int().min(0).default(1),
  maxYearsKept: z.number().int().min(0).nullable().default(null),
  rookieDraft: z.boolean().default(false),
  futurePickTrading: z.number().int().min(0).max(3).default(0),
});

// ── Governance ────────────────────────────────────────────────────────────

export const LockableSection = z.enum([
  'scoring',
  'roster',
  'lineup',
  'format',
  'schedule',
  'playoffs',
  'waivers',
  'trades',
  'keepers',
]);

export const GovernanceSettings = z.object({
  changesRequire: z
    .enum(['commissioner', 'majority-vote', 'supermajority-vote'])
    .default('commissioner'),
  supermajorityFraction: z
    .number()
    .min(0.5)
    .max(1)
    .default(2 / 3),
  lockedAfterDraft: z.array(LockableSection).default(['scoring', 'roster', 'format']),
  scoringChangesApply: z.enum(['future-periods', 'whole-season']).default('future-periods'),
});

// ── Fun: league awards, and account achievements ──────────────────────────
//
// TWO DIFFERENT THINGS, AND THE SHAPE SAYS SO SINCE VERSION 2. Awards belong
// to a league and a season's results; achievements belong to an account and
// can come at almost any time.
//
// Version 1 kept both in one `fun.awards` object with a boolean called
// `achievements` inside it, which reads as though an achievement were a kind
// of award. It is not: an award is won once per league per season and somebody
// else loses it; an achievement is earned once ever, across every league, and
// nobody is beaten (ACHIEVEMENTS.md). They have different scopes, different
// screens, and different answers to whether an unlock belongs in league chat —
// which is why `postToChat` is now a flag on each rather than one flag on both.
//
// `perPeriod` rather than `weekly`: "week" is "period" everywhere, so daily
// lineup sports fit without rework (CLAUDE.md). This was the last field in a
// stored shape still carrying the old vocabulary (AUDIT-2026-09-21).

export const LeagueAwardSettings = z.object({
  /** Hand out this period's league awards when the period ends. */
  perPeriod: z.boolean().default(true),
  /** Hand out season league awards when the championship is final. */
  perSeason: z.boolean().default(true),
  /** Award cards and the champion card post to league chat. */
  postToChat: z.boolean().default(true),
  /** League award ids this league turned off. */
  disabled: z.array(z.string().min(1)).default([]),
  /** Custom names by id: { 'low-score': 'The Sack of Shame' }. */
  names: z.record(z.string(), z.string().trim().min(1).max(40)).default({}),
});

export const AchievementSettings = z.object({
  /** Track account achievements earned through this league's play. */
  enabled: z.boolean().default(true),
  /** Unlock lines post to league chat. Separate from the award cards. */
  postToChat: z.boolean().default(true),
  /** Achievement ids this league turned off. */
  disabled: z.array(z.string().min(1)).default([]),
});

const LEAGUE_AWARD_DEFAULTS = {
  perPeriod: true,
  perSeason: true,
  postToChat: true,
  disabled: [],
  names: {},
};

const ACHIEVEMENT_DEFAULTS = { enabled: true, postToChat: true, disabled: [] };

/**
 * The weekly hat (PLAN-2026-10-30-the-weekly-hat, RULES_SCHEMA §15b): the
 * week's fewest points wear a hat the week's most points pick, until the next
 * week's. On by default, and arriving with its default, so every version
 * stored before 2026-10-30 reads with it on.
 */
export const HatSettings = z.object({
  enabled: z.boolean().default(true),
});

const HAT_DEFAULTS = { enabled: true };

export const FunSettings = z.object({
  leagueAwards: LeagueAwardSettings.default(LEAGUE_AWARD_DEFAULTS),
  achievements: AchievementSettings.default(ACHIEVEMENT_DEFAULTS),
  hat: HatSettings.default(HAT_DEFAULTS),
});

export const FUN_DEFAULTS = {
  leagueAwards: LEAGUE_AWARD_DEFAULTS,
  achievements: ACHIEVEMENT_DEFAULTS,
  hat: HAT_DEFAULTS,
};

// ── Helper tools (RULES_SCHEMA §15c, PLAN-2026-10-29-helper-tools-switch) ──
//
// The helpers a league may switch off, every one on by default. Off withholds
// what that helper shows a manager and nothing else: no action, no screen, no
// score, and never how the bots play. The trade read-out and the lineup total
// are built from our projections, so they are off while projections are,
// whatever their own switch says (`effectiveTools`).

export const ToolSettings = z.object({
  /** Our projected points wherever a manager could act on them. */
  projections: z.boolean().default(true),
  /** Worth a look, the suggestions read, trending and the trade partners' shortlist. */
  suggestions: z.boolean().default(true),
  /** The read-out and each side's worth on a trade. */
  tradeAnalysis: z.boolean().default(true),
  /** The starters' projected total on the lineup. */
  sandbox: z.boolean().default(true),
});

export const TOOL_DEFAULTS = {
  projections: true,
  suggestions: true,
  tradeAnalysis: true,
  sandbox: true,
};

// ── Top level ─────────────────────────────────────────────────────────────

export const LeagueSettings = z.object({
  schemaVersion: z.literal(SETTINGS_SCHEMA_VERSION),
  sport: SportIdSchema,
  season: z.number().int().min(2020).max(2100),
  identity: IdentitySettings,
  format: FormatSettings,
  scoring: ScoringConfig,
  categories: CategoryConfig.nullable().default(null),
  roster: RosterConfig,
  lineup: LineupConfig,
  schedule: ScheduleSettings,
  playoffs: PlayoffSettings,
  tiebreakers: TiebreakerSettings,
  waivers: WaiverSettings,
  acquisitions: AcquisitionLimits,
  trades: TradeSettings,
  draft: DraftSettings,
  keepers: KeeperSettings,
  governance: GovernanceSettings,
  /** A stored version without this section parses with the defaults. */
  fun: FunSettings.default(FUN_DEFAULTS),
  /** The same: every version before 2026-10-29 reads with every helper on. */
  tools: ToolSettings.default(TOOL_DEFAULTS),
});

export type LeagueSettings = z.infer<typeof LeagueSettings>;
export type LeagueSettingsInput = z.input<typeof LeagueSettings>;
export type ScoringConfig = z.infer<typeof ScoringConfig>;
export type CategoryConfig = z.infer<typeof CategoryConfig>;
export type RosterConfig = z.infer<typeof RosterConfig>;
export type LineupConfig = z.infer<typeof LineupConfig>;
export type FormatSettings = z.infer<typeof FormatSettings>;
export type ScheduleSettings = z.infer<typeof ScheduleSettings>;
export type PlayoffSettings = z.infer<typeof PlayoffSettings>;
export type TiebreakerSettings = z.infer<typeof TiebreakerSettings>;
export type WaiverSettings = z.infer<typeof WaiverSettings>;
export type TradeSettings = z.infer<typeof TradeSettings>;
export type DraftSettings = z.infer<typeof DraftSettings>;
export type KeeperSettings = z.infer<typeof KeeperSettings>;
export type GovernanceSettings = z.infer<typeof GovernanceSettings>;
export type FunSettings = z.infer<typeof FunSettings>;
export type LeagueAwardSettings = z.infer<typeof LeagueAwardSettings>;
export type AchievementSettings = z.infer<typeof AchievementSettings>;
export type ToolSettings = z.infer<typeof ToolSettings>;

// ── Reading a stored settings version ─────────────────────────────────────

/**
 * A settings blob out of `league_settings_versions`, upgraded then validated.
 *
 * USE THIS FOR ANYTHING THAT CAME OUT OF THE DATABASE. `LeagueSettings` is the
 * shape we write today and is deliberately strict about `schemaVersion`, so
 * parsing a stored row with it refuses every version but the newest — which is
 * right for a write and wrong for a read.
 *
 * NOTHING IS REWRITTEN. A settings version is the record of what the league
 * agreed to at the time, so an old row stays exactly as it was stored and is
 * upgraded on the way out. That also means there is no D1 migration behind a
 * settings change: the only cost is this function.
 */
function upgrade(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw;
  const settings = { ...(raw as Record<string, unknown>) };
  if (settings.schemaVersion === 1) upgradeAwards(settings);
  if (settings.schemaVersion === 2) upgradeDraft(settings);
  return settings;
}

/**
 * v2 → v3: the draft's one `type` becomes `format`, `order` and `mode`, the
 * seat order's `order` becomes `seatOrder`, and the clock's `autopick`
 * becomes `whenTheClockExpires` (BACKLOG D1).
 *
 * THE MODE A STORED DRAFT GETS is the one that ran it. Every pick-order draft
 * stored before version 3 was run by the clock with nobody present, the only
 * runner there was, so it is `autopick`. `offline` was the mode version 3
 * calls `manual`, entered by the commissioner. An auction, which nothing ran
 * or let anybody choose, is `live`, the only way one is designed to run.
 */
function upgradeDraft(settings: Record<string, unknown>): void {
  const old = settings.draft;
  if (typeof old === 'object' && old !== null) {
    const { type, order, autopick, ...rest } = old as Record<string, unknown>;
    const draft: Record<string, unknown> = { ...rest };
    if (type === 'auction') Object.assign(draft, { format: 'auction', order: null, mode: 'live' });
    else if (type === 'offline')
      Object.assign(draft, { format: 'pick', order: 'snake', mode: 'manual' });
    else Object.assign(draft, { format: 'pick', order: type, mode: 'autopick' });
    if (order !== undefined) draft.seatOrder = order;
    if (autopick !== undefined) draft.whenTheClockExpires = autopick;
    settings.draft = draft;
  }
  settings.schemaVersion = SETTINGS_SCHEMA_VERSION;
}

function upgradeAwards(settings: Record<string, unknown>): void {
  // v1 → v2: one `fun.awards` object becomes `fun.leagueAwards` beside
  // `fun.achievements`, and `weekly`/`season` take the period vocabulary.
  const fun = { ...((settings.fun as Record<string, unknown> | undefined) ?? {}) };
  const old = (fun.awards as Record<string, unknown> | undefined) ?? {};
  fun.leagueAwards = {
    perPeriod: old.weekly ?? true,
    perSeason: old.season ?? true,
    postToChat: old.postToChat ?? true,
    disabled: old.disabled ?? [],
    names: old.names ?? {},
  };
  // One flag governed award cards AND unlock lines in v1, so both inherit it.
  // `disabled` held award ids and achievement ids in one array, and nothing in
  // the blob says which is which — so both sides inherit the whole list. It is
  // safe: each side only ever looks up ids from its own catalog, so a stray id
  // matches nothing rather than turning the wrong thing off.
  fun.achievements = {
    enabled: old.achievements ?? true,
    postToChat: old.postToChat ?? true,
    disabled: old.disabled ?? [],
  };
  delete fun.awards;
  settings.fun = fun;
  settings.schemaVersion = 2;
}

export const StoredLeagueSettings = z.preprocess(upgrade, LeagueSettings);
export type UnitRule = z.infer<typeof UnitRule>;
export type BonusRule = z.infer<typeof BonusRule>;
export type TierRule = z.infer<typeof TierRule>;
